import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Save, RestoreFromTrash } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import apiService from '../services/api';

interface UserSettings {
  // Notifications
  emailNotifications: boolean;
  smsNotifications: boolean;
  desktopNotifications: boolean;
  membershipExpiryNotifications: boolean;
  
  // System Preferences
  autoBackup: boolean;
  realtimeSync: boolean;
  defaultMembershipType: string;
  defaultContractPeriod: string;
  
  // Gym Information
  gymName: string;
  gymAddress: string;
  gymPhone: string;
  gymEmail: string;
  gymWebsite: string;
  
  // Email Settings
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpFrom: string;
  
  // Contract Settings
  defaultMonthlyFee: number;
  defaultJoiningFee: number;
  contractTerms: string;
  
  // Display Settings
  dateFormat: string;
  timeFormat: string;
  currency: string;
  timezone: string;
}

const Settings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { control, handleSubmit, reset, formState: { errors } } = useForm<UserSettings>({
    defaultValues: {
      // Notifications
      emailNotifications: true,
      smsNotifications: false,
      desktopNotifications: false,
      membershipExpiryNotifications: true,
      
      // System Preferences
      autoBackup: true,
      realtimeSync: true,
      defaultMembershipType: 'Tenant',
      defaultContractPeriod: '12 months',
      
      // Gym Information
      gymName: 'Legends Fitness Center',
      gymAddress: '123 Fitness Street, Gym City, GC 12345',
      gymPhone: '(555) 123-4567',
      gymEmail: 'info@legendsfitness.com',
      gymWebsite: 'www.legendsfitness.com',
      
      // Email Settings
      smtpHost: 'smtp.gmail.com',
      smtpPort: 587,
      smtpUser: '',
      smtpFrom: 'noreply@legendsfitness.com',
      
      // Contract Settings
      defaultMonthlyFee: 49.99,
      defaultJoiningFee: 99.99,
      contractTerms: `Member agrees to follow all gym rules and regulations
Member is responsible for any damage to equipment
Membership is non-transferable and non-refundable
Member must provide 30-day notice for cancellation
Gym hours are Monday-Friday 5:00 AM - 11:00 PM, Weekends 6:00 AM - 10:00 PM
Member agrees to use equipment safely and at their own risk`,
      
      // Display Settings
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12-hour',
      currency: 'USD',
      timezone: 'America/New_York',
    },
  });

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getUserSettings();
      if (response.settings) {
        reset(response.settings);
      }
    } catch (err: any) {
      // If settings don't exist, use defaults
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  }, [reset]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const onSubmit = async (data: UserSettings) => {
    try {
      setSaving(true);
      setError(null);
      await apiService.saveUserSettings(data);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values?')) {
      reset();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Settings saved successfully!
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Gym Information */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Gym Information
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
            gap={2}
          >
            <Box>
              <Controller
                name="gymName"
                control={control}
                rules={{ required: 'Gym name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Gym Name"
                    error={!!errors.gymName}
                    helperText={errors.gymName?.message}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Box>
            <Box>
              <Controller
                name="gymPhone"
                control={control}
                rules={{ required: 'Phone number is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone Number"
                    error={!!errors.gymPhone}
                    helperText={errors.gymPhone?.message}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" } }}>
              <Controller
                name="gymAddress"
                control={control}
                rules={{ required: 'Address is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Address"
                    error={!!errors.gymAddress}
                    helperText={errors.gymAddress?.message}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Box>
            <Box>
              <Controller
                name="gymEmail"
                control={control}
                rules={{ 
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    error={!!errors.gymEmail}
                    helperText={errors.gymEmail?.message}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Box>
            <Box>
              <Controller
                name="gymWebsite"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Website"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Box>
          </Box>
        </Paper>

        {/* Notifications */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Notifications
          </Typography>
          <Controller
            name="emailNotifications"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={field.value} />}
                label="Email notifications for new members"
              />
            )}
          />
          <br />
          <Controller
            name="membershipExpiryNotifications"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={field.value} />}
                label="Notifications for membership expiry"
              />
            )}
          />
          <br />
          <Controller
            name="smsNotifications"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={field.value} />}
                label="SMS notifications"
              />
            )}
          />
          <br />
          <Controller
            name="desktopNotifications"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={field.value} />}
                label="Real-time desktop notifications"
              />
            )}
          />
        </Paper>

        {/* System Preferences */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            System Preferences
          </Typography>
          <Controller
            name="autoBackup"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={field.value} />}
                label="Auto-backup member data"
              />
            )}
          />
          <br />
          <Controller
            name="realtimeSync"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={field.value} />}
                label="Enable real-time synchronization"
              />
            )}
          />
          
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
            gap={2}
            sx={{ mt: 2 }}
          >
            <Box>
              <Controller
                name="defaultMembershipType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Default Membership Type</InputLabel>
                    <Select {...field} label="Default Membership Type">
                      <MenuItem value="Home Owner (H/O)">Home Owner (H/O)</MenuItem>
                      <MenuItem value="Outside Member">Outside Member</MenuItem>
                      <MenuItem value="Tenant">Tenant</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
            <Box>
              <Controller
                name="defaultContractPeriod"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Default Contract Period</InputLabel>
                    <Select {...field} label="Default Contract Period">
                      <MenuItem value="1 month">1 Month</MenuItem>
                      <MenuItem value="3 months">3 Months</MenuItem>
                      <MenuItem value="6 months">6 Months</MenuItem>
                      <MenuItem value="12 months">12 Months</MenuItem>
                      <MenuItem value="24 months">24 Months</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
          </Box>
        </Paper>

        {/* Contract Settings */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Contract & Pricing Settings
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
            gap={2}
          >
            <Box>
              <Controller
                name="defaultMonthlyFee"
                control={control}
                rules={{ required: 'Monthly fee is required', min: 0 }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Default Monthly Fee"
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    error={!!errors.defaultMonthlyFee}
                    helperText={errors.defaultMonthlyFee?.message}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Box>
            <Box>
              <Controller
                name="defaultJoiningFee"
                control={control}
                rules={{ required: 'Joining fee is required', min: 0 }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Default Joining Fee"
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    error={!!errors.defaultJoiningFee}
                    helperText={errors.defaultJoiningFee?.message}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" } }}>
              <Controller
                name="contractTerms"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Default Contract Terms (one per line)"
                    multiline
                    rows={6}
                    fullWidth
                    helperText="Enter each term on a new line"
                  />
                )}
              />
            </Box>
          </Box>
        </Paper>

        {/* Email Settings */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Email Settings
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
            gap={2}
          >
            <Box>
              <Controller
                name="smtpHost"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="SMTP Host"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Box>
            <Box>
              <Controller
                name="smtpPort"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="SMTP Port"
                    type="number"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Box>
            <Box>
              <Controller
                name="smtpUser"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="SMTP Username"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Box>
            <Box>
              <Controller
                name="smtpFrom"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="From Email Address"
                    type="email"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Box>
          </Box>
        </Paper>

        {/* Display Settings */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Display Settings
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
            gap={2}
          >
            <Box>
              <Controller
                name="dateFormat"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Date Format</InputLabel>
                    <Select {...field} label="Date Format">
                      <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                      <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                      <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
            <Box>
              <Controller
                name="timeFormat"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Time Format</InputLabel>
                    <Select {...field} label="Time Format">
                      <MenuItem value="12-hour">12-hour (AM/PM)</MenuItem>
                      <MenuItem value="24-hour">24-hour</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
            <Box>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Currency</InputLabel>
                    <Select {...field} label="Currency">
                      <MenuItem value="USD">USD ($)</MenuItem>
                      <MenuItem value="EUR">EUR (€)</MenuItem>
                      <MenuItem value="GBP">GBP (£)</MenuItem>
                      <MenuItem value="CAD">CAD (C$)</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
            <Box>
              <Controller
                name="timezone"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Timezone</InputLabel>
                    <Select {...field} label="Timezone">
                      <MenuItem value="America/New_York">Eastern Time</MenuItem>
                      <MenuItem value="America/Chicago">Central Time</MenuItem>
                      <MenuItem value="America/Denver">Mountain Time</MenuItem>
                      <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                      <MenuItem value="UTC">UTC</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
          </Box>
        </Paper>

        {/* Action Buttons */}
        <Box display="flex" justifyContent="space-between" sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<RestoreFromTrash />}
            onClick={handleReset}
            disabled={saving}
          >
            Reset to Defaults
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default Settings;